import { useState, useEffect, useRef } from 'react';
import { useFetcher } from 'react-router';
import { Plus, ChevronLeft } from 'lucide-react';
import { BOARD_TERMS } from '~/config/mood-board-terminology';
import { Input } from '@rocco/components/input';
import { Button } from '@rocco/components/button';
import { usePersonalWorkspace } from '~/modules/user/hook/use-personal-workspace';
import { CREATE_BUILD_HUB_INSPIRATION_API_ROUTE } from '~/routes/remix-api.app.build-hub-inspiration-create';
import type { CreateOrUpdateInspirationMutation } from 'generated/graphql';
import type { DesignBoardViewModel } from '@rocco/ui/design-board/view-models/design-board';
import { useTracker } from '@archipro-website/tracker';

interface FollowSelectorFooterProps {
    onBoardCreated: (board: DesignBoardViewModel) => void;
}

/**
 * Footer with inline "Add New" form for the FollowSelector.
 * Creates a new private moodboard in the user's personal workspace.
 */
export const FollowSelectorFooter = ({
    onBoardCreated,
}: FollowSelectorFooterProps) => {
    const tracker = useTracker();
    const [isAdding, setIsAdding] = useState(false);
    const [boardName, setBoardName] = useState('');
    const [error, setError] = useState<string | null>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const fetcher = useFetcher<CreateOrUpdateInspirationMutation>();
    const personalWorkspace = usePersonalWorkspace();
    const lastProcessedBoardId = useRef<number | null>(null);

    const isSubmitting = fetcher.state !== 'idle';

    // Focus input when entering adding mode
    useEffect(() => {
        if (isAdding && inputRef.current) {
            inputRef.current.focus();
        }
    }, [isAdding]);

    // Handle fetcher response
    useEffect(() => {
        if (fetcher.data && fetcher.state === 'idle') {
            const result = fetcher.data.projectInspirationGroupUpsert;

            if (result.__typename === 'InspirationGroup') {
                const boardId = parseInt(result.ID, 10);

                // Prevent duplicate processing of the same board
                if (lastProcessedBoardId.current === boardId) {
                    return;
                }
                lastProcessedBoardId.current = boardId;

                // Map to DesignBoardViewModel
                const boardViewModel: DesignBoardViewModel = {
                    id: boardId,
                    title: result.Title,
                    urlSegment: result.URLSegment,
                    isNew: true,
                    pins: [],
                };

                // Reset form
                setIsAdding(false);
                setBoardName('');
                setError(null);

                // Notify parent
                onBoardCreated(boardViewModel);
            } else {
                // Handle error types (NotAuthorizedError, etc.)
                setError(
                    'Message' in result
                        ? result.Message
                        : 'Failed to create board',
                );
            }
        }
    }, [fetcher.data, fetcher.state, onBoardCreated]);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        if (!boardName.trim()) {
            setError('Please enter a board name');
            return;
        }

        if (!personalWorkspace) {
            setError('Personal workspace not found');
            return;
        }

        setError(null);
        void tracker.log('DesignBoardCreateSubmit', {
            url: new URL(window.location.href),
        });

        const formData = new FormData();
        formData.append('id', personalWorkspace.ID.toString());
        formData.append('title', boardName.trim());
        formData.append('description', '');
        formData.append('accessMode', 'restricted');
        formData.append('accessRole', 'Editor');

        fetcher.submit(formData, {
            method: 'post',
            action: CREATE_BUILD_HUB_INSPIRATION_API_ROUTE,
        });
    };

    const handleCancel = () => {
        setIsAdding(false);
        setBoardName('');
        setError(null);
    };

    if (isAdding) {
        return (
            <div className="flex-shrink-0 border-t border-gray-200 px-4.5 py-2 pb-[calc(0.5rem+env(safe-area-inset-bottom))]">
                <form onSubmit={handleSubmit} id="add-board-form">
                    <div className="-ml-1 flex items-center p-1 pr-3">
                        <button
                            type="button"
                            onClick={handleCancel}
                            disabled={isSubmitting}
                        >
                            <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-lg bg-[#E5E5E1]">
                                <ChevronLeft className="h-5 w-5 text-gray-600" />
                            </div>
                        </button>
                        <Input
                            ref={inputRef}
                            type="text"
                            value={boardName}
                            onChange={(e) => setBoardName(e.target.value)}
                            placeholder={BOARD_TERMS.nameYourBoard}
                            disabled={isSubmitting}
                            color="transparent"
                            className="h-11 flex-1 pl-3 text-[16px] md:text-sm"
                        />
                        <Button
                            type="submit"
                            disabled={isSubmitting || !boardName.trim()}
                            color="dark"
                            size="narrow"
                            isLoading={isSubmitting}
                            className="min-w-[4.5rem] font-medium"
                        >
                            Create
                        </Button>
                    </div>
                    {error && (
                        <p className="mt-2 text-sm text-red-600">{error}</p>
                    )}
                </form>
            </div>
        );
    }

    return (
        <div className="flex-shrink-0 border-t border-gray-200 px-4.5 py-2 pb-[calc(0.5rem+env(safe-area-inset-bottom))]">
            <button
                type="button"
                onClick={() => setIsAdding(true)}
                className="-ml-1 flex w-full items-center gap-3 rounded-lg p-1 pr-3 text-left transition-colors hover:bg-[#F1f1f1] focus:bg-[#F1f1f1] focus:outline-none"
            >
                <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-lg bg-[#E5E5E1]">
                    <Plus className="h-5 w-5 text-gray-600" />
                </div>
                <span className="text-sm font-medium text-gray-900">
                    Add New
                </span>
            </button>
        </div>
    );
};
